<script lang="ts">
    import cn from 'classnames';
    import { configService } from '$lib/services';
    import { sendRegistrationEvent } from '$lib/services/gtm/methods';
    import { WithIntersectionPlaceholder } from '$lib/utils';

    import type { StoryFeedItem } from '@api/story/types';
    import { sendRegistrationStats } from '@api/user/stats';
    import cs from '@components/common/common.module.css';
    import { FeatureToggle } from '@components/common/feature-toggle';
    import { Video } from '@components/common/video';
    import { CustomIcon, getIcon } from '@components/icons';
    import { globalStore } from '@components/stores';
    import { StoryFeed, storyFeedStore } from '@components/story/feed';
    import { dialogStore } from '@components/ui/dialog/store';
    import { LL } from '@i18n/i18n-svelte';
    import { ExperimentGroup, ExperimentType } from '@shared/experiment';
    import { URLS } from '@shared/routing';
    import { EventType, Feature, SignupContext, UserGender } from '@shared/types';
    import { Button, ButtonTypes } from '@uikit/button';
    import { Font, FontTypes } from '@uikit/font';
    import { SvgIcon } from '@uikit/svg-icon';
    import { Color } from '@uikit/svg-icon/types';

    import { MIN_STORIES_COUNT } from './constants';
    import type { MainGenderBlockContent } from './types';

    import s from './gender-block-stories.module.css';

    import { goto } from '$app/navigation';
    import { page } from '$app/stores';

    export let stories: StoryFeedItem[] = [];
    export let content: MainGenderBlockContent;
    export let isFakeOnline = false;

    let activeStoryEl: HTMLVideoElement | null = null;

    function shouldRenderByIntersection(index: number): boolean {
        return !configService.get('isBot') && index > 2 && !$globalStore.isDesktopSite;
    }

    function closeHandler() {
        dialogStore.closeDialog();
        goto(URLS.unauthorizedStart());
    }

    function signup(gender?: UserGender) {
        const event = !gender ? EventType.MainButtonClick : {
            [UserGender.Male]: EventType.MainButtonClickMan,
            [UserGender.Female]: EventType.MainButtonClickWoman,
        }[gender];
        sendRegistrationEvent({ event });
        sendRegistrationStats(event, undefined, $globalStore.isDesktopSite);
        dialogStore.showSignup({ gender, place: SignupContext.Main }, closeHandler);
    }

    async function gotoStories(clickedStoryId?: string) {
        if (clickedStoryId) {
            storyFeedStore.setLastWatchedId(clickedStoryId);
        }
        await goto(URLS.storyOpen(clickedStoryId));
    }

    async function playStory(event: Event) {
        await pauseStory();

        const storyEl = event.target as HTMLVideoElement;
        try {
            await storyEl.play();
            activeStoryEl = storyEl;
        } catch (error) {
            console.error(error);
        }
    }

    async function pauseStory() {
        if (activeStoryEl) {
            try {
                activeStoryEl.pause();
                activeStoryEl = null;
            } catch (error) {
                console.error(error);
            }
        }
    }
</script>

<div class={cn(s.main, { [s.safe]: $page.url.pathname.includes(URLS.talkToStrangers()) })}>
    <div class={cn(cs.mainContainer, s.container)}>
        <hgroup class={s.titleContainer}>
            <Font
                type={FontTypes.Type.h1}
                tagName={FontTypes.TagName.h1}
                isBold
                className={s.title}
            >
                {content.title}
            </Font>
            {#if content.subtitle}
                <Font
                    type={FontTypes.Type.h3}
                    tagName={FontTypes.TagName.p}
                    className={s.subtitle}
                >
                    {@html content.subtitle}
                </Font>
            {/if}
        </hgroup>

        <div class={s.buttonsContainer}>
            {#if configService.getExperimentGroup(ExperimentType.FF012) === ExperimentGroup.B}
                <Button
                    classes={s.button}
                    color={ButtonTypes.Color.Brand}
                    on:click={() => signup()}
                >
                    {content.buttonStartLabel}
                </Button>
            {:else}
                <Button
                    attrs={{ 'data-testid': 'femaleButton' }}
                    classes={cn(s.button, s.femaleButton)}
                    icon={getIcon(CustomIcon.female)}
                    on:click={() => signup(UserGender.Female)}
                >
                    {content.buttonFemaleLabel}
                </Button>
                <Button
                    attrs={{ 'data-testid': 'maleButton' }}
                    classes={cn(s.button, s.maleButton)}
                    icon={getIcon(CustomIcon.male)}
                    on:click={() => signup(UserGender.Male)}
                >
                    {content.buttonMaleLabel}
                </Button>
            {/if}
        </div>

        <FeatureToggle feature={Feature.Stories}>
            {#if stories?.length >= MIN_STORIES_COUNT}
                <section class={s.storiesContainer}>
                    <div class={s.storiesHeader}>
                        <Font
                            type={FontTypes.Type.h3}
                            tagName={FontTypes.TagName.h2}
                            isBold
                            className={s.storiesHeaderText}
                        >
                            {$LL.main.storiesHeader()} 🔥
                        </Font>
                        <Font
                            type={FontTypes.Type.body}
                            tagName={FontTypes.TagName.div}
                            isBold
                            className={s.storiesHeaderAction}
                            on:click={() => gotoStories()}
                        >
                            {$LL.main.storiesAction()}
                            <SvgIcon
                                icon={getIcon(CustomIcon.arrowRight)}
                                color={Color.white}
                            />
                        </Font>
                    </div>
                    <div class={s.storiesContent}>
                        {#each stories as story, index (story.id)}
                            <WithIntersectionPlaceholder
                                shouldRenderByIntersection={shouldRenderByIntersection(index)}
                                rootMargin={100}
                            >
                                <article
                                    class={cn(s.storyWrapper, s.safariBg)}
                                    style="--safari-bg-img: url({story.previewImgUrl});"
                                >
                                    <Video
                                        poster={story.feedImgUrl ?? story.previewImgUrl}
                                        src={story.videoUrl}
                                        covered
                                        muted
                                        loop
                                        playsinline
                                        preload="none"
                                        on:click={() => gotoStories(story.id)}
                                        on:touchstart={playStory}
                                        on:mouseenter={playStory}
                                        on:mouseleave={pauseStory}
                                    ></Video>
                                    <div class={s.modelInfo}>
                                        <Font
                                            type={FontTypes.Type.body}
                                            tagName={FontTypes.TagName.h3}
                                            isBold
                                            className={s.modelName}
                                        >
                                            {story.operatorDisplayName}
                                        </Font>
                                        {#if story.isOnline || isFakeOnline}
                                            <Font
                                                type={FontTypes.Type.body}
                                                tagName={FontTypes.TagName.p}
                                                className={cn(s.modelStatus, s.online)}
                                            >
                                                {$LL.favorites.online()}
                                            </Font>
                                        {:else}
                                            <Font
                                                type={FontTypes.Type.body}
                                                tagName={FontTypes.TagName.p}
                                                className={cn(s.modelStatus, s.offline)}
                                            >
                                                {$LL.favorites.offline()}
                                            </Font>
                                        {/if}
                                    </div>
                                </article>
                            </WithIntersectionPlaceholder>
                        {/each}
                    </div>
                </section>
            {/if}
        </FeatureToggle>
    </div>
</div>

<FeatureToggle feature={Feature.Stories}>
    <StoryFeed />
</FeatureToggle>
